import axios from "axios";
import { API_URL } from "../constants";

/**
 * Per-sphere approval of an employee's position-derived pareigybės
 * (docs/prd/positions-catalogue.md §1 rules 7–8, §5). After a hire contract,
 * a position-changing amendment or a profile edit, every sphere whose
 * proposal is non-empty/changed waits for one of its responsibles (or a
 * Master); nothing is applied for that sphere before the approval.
 */

export type WsApprovalKind = "hire" | "amendment" | "profile";
export type WsApprovalStatus = "pending" | "approved" | "auto_approved" | "superseded";

export interface WsApprovalPerson {
  id: string;
  name: string;
}

/** One row of GET /work-security/approvals. */
export interface WsApprovalRow {
  id: string;
  kind: WsApprovalKind;
  status: WsApprovalStatus;
  user: WsApprovalPerson;
  /** Names of the employee's positions this approval is about, ordered. */
  positions: string[];
  company?: string;
  area: { id: string; name: string };
  createdAt: number | string;
  onboardingId?: string | null;
  amendmentId?: string | null;
  /** Not in the §5 list shape; read when the backend sends it (detail view of a decided approval). */
  approvedBy?: WsApprovalPerson | null;
  approvedAt?: number | string | null;
}

export interface WsApprovalProposedRole {
  roleId: string;
  name: string;
  requiredAtHiring: boolean;
  hasActiveDocument: boolean;
}

export interface WsApprovalRoleRef {
  roleId: string;
  name: string;
}

/** GET /work-security/approvals/:id — deliberately without any questionnaire / salary data. */
export interface WsApprovalDetail extends WsApprovalRow {
  proposal: WsApprovalProposedRole[];
  currentIndividual: WsApprovalRoleRef[];
  availableAreaRoles: WsApprovalRoleRef[];
}

/** Per-sphere status for the Personalas card (hire step and amendments). */
export interface OnboardingApprovalStatusRow {
  id: string;
  kind?: WsApprovalKind;
  status: WsApprovalStatus;
  area: { id: string; name: string };
  isBlocking?: boolean;
  amendmentId?: string | null;
  approvedBy?: WsApprovalPerson | null;
  approvedAt?: number | string | null;
  responsibles?: WsApprovalPerson[];
}

function unwrapList<T>(data: any, key: string): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && Array.isArray(data[key])) return data[key] as T[];
  return [];
}

/**
 * The backend decides which spheres the caller manages (Master: all);
 * `areaId` narrows further and follows the useAreaScope hard rule — pass it
 * only when a concrete sphere is selected.
 */
export async function fetchWsApprovals(
  companyId: string,
  params: { status?: WsApprovalStatus; areaId?: string } = { status: "pending" }
): Promise<WsApprovalRow[]> {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.areaId) query.set("areaId", params.areaId);
  const qs = query.toString();
  const res = await axios.get(
    `${API_URL}/${companyId}/work-security/approvals${qs ? `?${qs}` : ""}`
  );
  return unwrapList<WsApprovalRow>(res.data, "approvals");
}

export async function fetchWsApproval(
  companyId: string,
  approvalId: string
): Promise<WsApprovalDetail> {
  const res = await axios.get(
    `${API_URL}/${companyId}/work-security/approvals/${approvalId}`
  );
  const data = res.data && res.data.approval ? res.data.approval : res.data;
  return {
    ...data,
    positions: Array.isArray(data?.positions) ? data.positions : [],
    proposal: Array.isArray(data?.proposal) ? data.proposal : [],
    currentIndividual: Array.isArray(data?.currentIndividual) ? data.currentIndividual : [],
    availableAreaRoles: Array.isArray(data?.availableAreaRoles) ? data.availableAreaRoles : [],
  };
}

/** Approves the sphere's proposal and adds the chosen roles as individual ones. */
export async function approveWsApproval(
  companyId: string,
  approvalId: string,
  individualRoleIds: string[]
): Promise<void> {
  await axios.post(
    `${API_URL}/${companyId}/work-security/approvals/${approvalId}/approve`,
    { individualRoleIds }
  );
}

/**
 * The per-sphere status of one onboarding. §5 allows the backend to serve it
 * either at GET /onboarding/:id/approvals or inside work-security-status as
 * `approvals`; the dedicated endpoint is tried first, and a 404 (not built)
 * falls back to whatever the caller already got from work-security-status.
 */
export async function fetchOnboardingApprovals(
  companyId: string,
  onboardingId: string,
  fallback?: OnboardingApprovalStatusRow[] | null
): Promise<OnboardingApprovalStatusRow[]> {
  try {
    const res = await axios.get(
      `${API_URL}/${companyId}/onboarding/${onboardingId}/approvals`
    );
    return unwrapList<OnboardingApprovalStatusRow>(res.data, "approvals");
  } catch (e: any) {
    if (e?.response?.status === 404 && fallback) return fallback;
    if (e?.response?.status === 404) return [];
    throw e;
  }
}
